<template>
  <div class="guest-timeline">
    <p class="detail-title">
      <b-icon pack="fas" icon="stream" size="is-small"></b-icon>
      {{ t.title }}
    </p>

    <b-loading :is-full-page="false" :active="cargando"></b-loading>

    <b-message v-if="error" type="is-danger" size="is-small">{{ t.error }}</b-message>

    <p v-else-if="!cargando && !eventos.length" class="has-text-grey is-size-7">{{ t.empty }}</p>

    <ul v-else class="timeline-list">
      <li
        v-for="(evento, i) in visibles"
        :key="i"
        class="timeline-item"
        :class="{ 'is-muted': estilo(evento).muted }"
      >
        <span class="timeline-dot" :class="estilo(evento).color">
          <b-icon pack="fas" :icon="estilo(evento).icon" size="is-small"></b-icon>
        </span>
        <div class="timeline-body">
          <p class="timeline-when">
            {{ fecha(evento.fecha) }}
            <span v-if="quienTexto(evento)" class="timeline-who">· {{ quienTexto(evento) }}</span>
          </p>
          <p class="timeline-title">{{ titulo(evento) }}</p>
          <p v-for="(linea, j) in detalle(evento)" :key="'d' + j" class="timeline-detail">{{ linea }}</p>
          <p
            v-for="(consecuencia, k) in evento.consecuencias"
            :key="'c' + k"
            class="timeline-consequence"
          >
            ↳ {{ titulo(consecuencia) }}<span v-if="detalle(consecuencia).length">: {{ detalle(consecuencia).join(" ") }}</span>
          </p>
        </div>
      </li>
    </ul>
    <b-button
      v-if="eventos.length > LIMITE"
      size="is-small"
      type="is-text"
      class="timeline-more"
      @click="todos = !todos"
    >
      {{ todos ? t.showLess : reemplaza(t.showAll, { n: eventos.length }) }}
    </b-button>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import "moment-timezone";
import "moment/locale/es";
import i18n from "../../utils/i18n";

// Cargar el locale "es" lo vuelve el global de moment; el resto de la app usa inglés
moment.locale("en");

const API_URL = process.env.VUE_APP_API_URL;

const TEXTOS = {
  es: {
    title: "Línea de tiempo",
    empty: "Todavía no hay movimientos registrados.",
    error: "No se pudo cargar la línea de tiempo.",
    byStaff: "por {quien}",
    origins: { ssp: "desde GoHighLevel (SSP)", sistema: "automático", hubspot: "en HubSpot", sistemas: "corrección de Sistemas" },
    registro: "Se registró el guest",
    agendo: "Agendó su presentación",
    reagendo: "Cambió la fecha de su presentación",
    repetido: "SSP volvió a confirmar la misma fecha",
    cancelo: "Canceló su cita",
    falto: "No se presentó (reportado por SSP)",
    asistio: "Asistió a la presentación (reportado por SSP)",
    otraCita: {
      no_show: "Llegó un no-show de una cita anterior",
      appointment_cancelled: "Llegó la cancelación de una cita anterior",
      showed: "Llegó una asistencia de una cita anterior",
    },
    otraCitaDetalle: "No era su cita vigente.",
    califico: "Completó la encuesta de calificación",
    correccion: "Se corrigió la fecha de su cita",
    sesion: "Se cambió su sesión en el capturador",
    manifiesto: "Se capturó el manifiesto",
    manifiestoEditado: "Se modificó el manifiesto",
    manifiestoBorrado: "Se borró el manifiesto",
    manifiestoArchivado: "Se archivó el manifiesto anterior ({rating})",
    manifiestoArchivadoDetalle: "Era de la presentación del {sesion}.",
    etapa: "Tarjeta: {de} → {a}",
    etapaNueva: "Tarjeta creada en {a}",
    lost: "Perdido",
    reason: "Motivo",
    session: "sesión",
    otro: "Evento de SSP: {evento}",
    showAll: "Ver todo ({n})",
    showLess: "Ver menos",
  },
  en: {
    title: "Timeline",
    empty: "No activity recorded yet.",
    error: "The timeline could not be loaded.",
    byStaff: "by {quien}",
    origins: { ssp: "from GoHighLevel (SSP)", sistema: "automatic", hubspot: "in HubSpot", sistemas: "IT correction" },
    registro: "Guest registered",
    agendo: "Booked the presentation",
    reagendo: "Changed the presentation date",
    repetido: "SSP confirmed the same date again",
    cancelo: "Cancelled the appointment",
    falto: "Did not show up (reported by SSP)",
    asistio: "Attended the presentation (reported by SSP)",
    otraCita: {
      no_show: "A no-show arrived for a previous appointment",
      appointment_cancelled: "A cancellation arrived for a previous appointment",
      showed: "An attendance arrived for a previous appointment",
    },
    otraCitaDetalle: "It was not the current appointment.",
    califico: "Completed the qualification survey",
    correccion: "The appointment date was corrected",
    sesion: "Session changed in the capturador",
    manifiesto: "Manifest captured",
    manifiestoEditado: "Manifest updated",
    manifiestoBorrado: "Manifest cleared",
    manifiestoArchivado: "Previous manifest archived ({rating})",
    manifiestoArchivadoDetalle: "It belonged to the {sesion} presentation.",
    etapa: "Card: {de} → {a}",
    etapaNueva: "Card created in {a}",
    lost: "Lost",
    reason: "Reason",
    session: "session",
    otro: "SSP event: {evento}",
    showAll: "Show all ({n})",
    showLess: "Show less",
  },
};

const RATINGS = { noShow: "ratingNoShow", walkout: "ratingWalkout", showFail: "ratingShowFail", showWin: "ratingShowWin" };

const ESTILOS = {
  registro: { icon: "user-plus", color: "is-info" },
  agendo: { icon: "calendar-plus", color: "is-info" },
  reagendo: { icon: "calendar-alt", color: "is-warning" },
  repetido: { icon: "redo", color: "is-light", muted: true },
  cancelo: { icon: "calendar-times", color: "is-danger" },
  falto: { icon: "user-slash", color: "is-danger" },
  asistio: { icon: "user-check", color: "is-success" },
  otra_cita: { icon: "history", color: "is-light", muted: true },
  califico: { icon: "clipboard-check", color: "is-light" },
  correccion: { icon: "wrench", color: "is-dark" },
  sesion: { icon: "exchange-alt", color: "is-warning" },
  manifiesto: { icon: "clipboard-list", color: "is-primary" },
  manifiesto_archivado: { icon: "archive", color: "is-light", muted: true },
  etapa: { icon: "columns", color: "is-link" },
  otro: { icon: "circle", color: "is-light", muted: true },
};

export default {
  name: "GuestTimeline",
  props: {
    prospectoId: { type: String, required: true },
    iso: { type: String, default: "en" },
  },
  data() {
    return { eventos: [], cargando: false, error: false, todos: false, LIMITE: 8 };
  },
  computed: {
    t() {
      return TEXTOS[this.iso] || TEXTOS.en;
    },
    visibles() {
      return this.todos ? this.eventos : this.eventos.slice(0, this.LIMITE);
    },
  },
  watch: {
    prospectoId: { immediate: true, handler: "cargar" },
  },
  methods: {
    async cargar() {
      if (!this.prospectoId) return;
      this.cargando = true;
      this.error = false;
      try {
        const { data } = await axios.get(`${API_URL}/prospectos/${this.prospectoId}/traza`);
        this.eventos = data?.eventos || [];
      } catch (e) {
        console.error("Error loading guest timeline:", e);
        this.error = true;
        this.eventos = [];
      } finally {
        this.cargando = false;
      }
    },
    reemplaza(texto, valores) {
      return Object.entries(valores).reduce((s, [k, v]) => s.replace(`{${k}}`, v ?? "—"), texto);
    },
    fecha(ms) {
      if (!ms) return "—";
      const f = moment.tz(Number(ms), "America/Cancun").locale(this.iso === "es" ? "es" : "en");
      return f.format(this.iso === "es" ? "D MMM YYYY, h:mm A" : "MMM D, YYYY, h:mm A");
    },
    // Instante de una cita: día y hora de Cancún, que es la hora de las sesiones
    cita(ms) {
      if (!ms) return "—";
      const f = moment.tz(Number(ms), "America/Cancun").locale(this.iso === "es" ? "es" : "en");
      return f.format(this.iso === "es" ? "ddd D MMM, h:mm A" : "ddd, MMM D, h:mm A");
    },
    // Sesión del capturador: MeetingDate es el día a las 00:00 UTC y la hora va aparte
    sesion(s) {
      if (!s) return "—";
      const dia = s.fecha ? moment.utc(Number(s.fecha)).locale(this.iso === "es" ? "es" : "en").format(this.iso === "es" ? "ddd D MMM" : "ddd, MMM D") : "";
      return [dia, s.hora ? `${this.t.session} ${s.hora}` : ""].filter(Boolean).join(", ") || "—";
    },
    rating(valor) {
      const llave = RATINGS[valor];
      return llave && i18n[llave] ? i18n[llave][this.iso] : valor || "—";
    },
    estilo(evento) {
      return ESTILOS[evento.tipo] || ESTILOS.otro;
    },
    quienTexto(evento) {
      if (evento.quien) return this.reemplaza(this.t.byStaff, { quien: evento.quien });
      return this.t.origins[evento.origen] || "";
    },
    titulo(evento) {
      const d = evento.datos || {};
      switch (evento.tipo) {
        case "otra_cita":
          return this.t.otraCita[d.evento] || this.t.otraCita.no_show;
        case "manifiesto":
          if (!d.despues) return this.t.manifiestoBorrado;
          return d.antes ? this.t.manifiestoEditado : this.t.manifiesto;
        case "manifiesto_archivado":
          return this.reemplaza(this.t.manifiestoArchivado, { rating: this.rating(d.rating) });
        case "etapa":
          return d.de ? this.reemplaza(this.t.etapa, { de: d.de, a: d.a }) : this.reemplaza(this.t.etapaNueva, { a: d.a });
        case "otro":
          return this.reemplaza(this.t.otro, { evento: d.evento });
        default:
          return this.t[evento.tipo] || evento.tipo;
      }
    },
    detalle(evento) {
      const d = evento.datos || {};
      switch (evento.tipo) {
        case "agendo":
        case "repetido":
          return [this.cita(d.a)];
        case "reagendo":
        case "correccion":
          return d.de && d.de !== d.a ? [`${this.cita(d.de)}  →  ${this.cita(d.a)}`] : [this.cita(d.a)];
        case "cancelo":
        case "falto":
        case "asistio":
          return d.inicio ? [this.cita(d.inicio)] : [];
        case "otra_cita":
          return [d.inicio ? `${this.cita(d.inicio)}. ${this.t.otraCitaDetalle}` : this.t.otraCitaDetalle];
        case "sesion":
          return [`${this.sesion(d.de)}  →  ${this.sesion(d.a)}`];
        case "manifiesto": {
          const m = d.despues;
          if (!m) return [];
          const partes = [m.Perdido ? this.t.lost : this.rating(m.Rating), m.Clasificacion].filter((p) => p && p !== "—");
          const lineas = partes.length ? [partes.join(" · ")] : [];
          if (m.Perdido && m.MotivoPerdida) lineas.push(`${this.t.reason}: ${m.MotivoPerdida}`);
          return lineas;
        }
        case "manifiesto_archivado":
          return d.sesion ? [this.reemplaza(this.t.manifiestoArchivadoDetalle, { sesion: this.sesion(d.sesion) })] : [];
        default:
          return [];
      }
    },
  },
};
</script>

<style scoped>
.detail-title {
  font-weight: 700;
  font-size: 1rem;
  color: #363636;
  border-bottom: 1px solid #dbdbdb;
  padding-bottom: 0.35rem;
  margin: 1.25rem 0 0.75rem;
}
.timeline-more {
  margin-left: 0.85rem;
}
.guest-timeline {
  position: relative;
  min-height: 3rem;
  margin-bottom: 1.25rem;
}
.timeline-list {
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
  border-left: 2px solid #e5e7eb;
  margin-left: 0.85rem;
}
.timeline-item {
  position: relative;
  padding: 0 0 0.9rem 1.4rem;
}
.timeline-item.is-muted {
  opacity: 0.6;
}
.timeline-dot {
  position: absolute;
  left: -0.95rem;
  top: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #4b5563;
  border: 2px solid #fff;
}
.timeline-dot.is-info { background: #e0f2fe; color: #0369a1; }
.timeline-dot.is-warning { background: #fef3c7; color: #b45309; }
.timeline-dot.is-danger { background: #fee2e2; color: #b91c1c; }
.timeline-dot.is-success { background: #dcfce7; color: #15803d; }
.timeline-dot.is-primary { background: #ede9fe; color: #6d28d9; }
.timeline-dot.is-link { background: #e0e7ff; color: #3730a3; }
.timeline-dot.is-dark { background: #374151; color: #fff; }
.timeline-when {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.75rem;
}
.timeline-who {
  font-style: italic;
}
.timeline-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #111827;
}
.timeline-detail {
  font-size: 0.85rem;
  color: #374151;
}
.timeline-consequence {
  font-size: 0.8rem;
  color: #4b5563;
  margin-top: 0.15rem;
}
</style>
